import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '../catalyst/button'
import { Checkbox } from '../catalyst/checkbox'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../catalyst/dialog'
import { Field as FormField, FieldGroup, Label } from '../catalyst/fieldset'
import { Input } from '../catalyst/input'
import { Text } from '../catalyst/text'
import { useAuth } from '../lib/auth'
import { issueFor, useCanadaGapFarmStatus, useEntryCheck } from '../lib/canadagap'
import { ComplianceBadge } from '../lib/compliance-badge'
import { usePackingRecords, type PackingRecordInput } from '../lib/farm-level-logs'
import { byDateDesc, FarmLogLayout, orNull, text, useSavedEntries, type DialogState } from '../lib/farm-log-layout'
import type { PackingRecord } from '../lib/types'
import { checkBadgeProps, EntryCheckPanel, InputWarning } from './entry-check'
import { EntryIssues } from './entry-issues'

const FORM_NAME = 'Packing, Repacking, Storing and Brokerage of Market Product'

function PackingRecordDialog({ onClose, initial, onSave }: { onClose: () => void; initial?: PackingRecord; onSave: (input: PackingRecordInput) => Promise<unknown> }) {
  const [packedOn, setPackedOn] = useState(initial?.packedOn ?? '')
  const [handledByName, setHandledByName] = useState(text(initial?.handledByName))
  const [productVariety, setProductVariety] = useState(text(initial?.productVariety))
  const [harvestedOn, setHarvestedOn] = useState(text(initial?.harvestedOn))
  const [fieldOrLotRef, setFieldOrLotRef] = useState(text(initial?.fieldOrLotRef))
  const [incomingLotId, setIncomingLotId] = useState(text(initial?.incomingLotId))
  const [outgoingLotId, setOutgoingLotId] = useState(text(initial?.outgoingLotId))
  const [quantity, setQuantity] = useState(initial?.quantity != null ? String(initial.quantity) : '')
  const [quantityUnit, setQuantityUnit] = useState(text(initial?.quantityUnit))
  const [primaryPackaging, setPrimaryPackaging] = useState(text(initial?.primaryPackaging))
  const [secondaryPackaging, setSecondaryPackaging] = useState(text(initial?.secondaryPackaging))
  const [storedOn, setStoredOn] = useState(text(initial?.storedOn))
  // Attestations start unticked: ticking one is an explicit statement, like H1 "label followed".
  const [phiMet, setPhiMet] = useState(initial?.phiMet ?? false)
  const [siteAssessed, setSiteAssessed] = useState(initial?.siteAssessed ?? false)
  const [packagingChecked, setPackagingChecked] = useState(initial?.packagingChecked ?? false)
  const [rotatedAppropriately, setRotatedAppropriately] = useState(initial?.rotatedAppropriately ?? false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Everything but the date, as saved; also what the CanadaGAP check runs on.
  const fields = {
    handledByName: orNull(handledByName),
    productVariety: orNull(productVariety),
    harvestedOn: orNull(harvestedOn),
    fieldOrLotRef: orNull(fieldOrLotRef),
    incomingLotId: orNull(incomingLotId),
    outgoingLotId: orNull(outgoingLotId),
    quantity: quantity ? Number(quantity) : null,
    quantityUnit: orNull(quantityUnit),
    primaryPackaging: orNull(primaryPackaging),
    secondaryPackaging: orNull(secondaryPackaging),
    storedOn: orNull(storedOn),
    phiMet,
    siteAssessed,
    packagingChecked,
    rotatedAppropriately,
  }
  const { org } = useAuth()
  const { farmId } = useParams<{ farmId: string }>()
  const check = useEntryCheck(org?.id, farmId, { form: 'q', entry: { packedOn: packedOn || null, ...fields } })

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!packedOn) return
    setSubmitting(true)
    setError(null)
    try {
      await onSave({ packedOn, ...fields })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save packing record.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>{initial ? 'Edit packing record' : 'Record packing'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <ComplianceBadge framework="CanadaGAP" formCode="Q" formName={FORM_NAME} {...checkBadgeProps(check)} />
        </div>
        <DialogBody>
          {error && <Text className="mb-4 text-red-600 dark:text-red-400">{error}</Text>}
          <FieldGroup>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField>
                <Label>Packing date</Label>
                <Input type="date" value={packedOn} onChange={(e) => setPackedOn(e.target.value)} required autoFocus />
              </FormField>
              <FormField>
                <Label>Packed / stored by</Label>
                <Input value={handledByName} onChange={(e) => setHandledByName(e.target.value)} />
                <InputWarning result={check} input="handledByName" />
              </FormField>
              <FormField>
                <Label>Variety</Label>
                <Input value={productVariety} onChange={(e) => setProductVariety(e.target.value)} placeholder="e.g. Duke" />
                <InputWarning result={check} input="productVariety" />
              </FormField>
              <FormField>
                <Label>Harvest date</Label>
                <Input type="date" value={harvestedOn} onChange={(e) => setHarvestedOn(e.target.value)} />
                <InputWarning result={check} input="harvestedOn" />
              </FormField>
              <FormField>
                <Label>Field / block / bin tag</Label>
                <Input value={fieldOrLotRef} onChange={(e) => setFieldOrLotRef(e.target.value)} placeholder="Same as on Form P2" />
              </FormField>
              <FormField>
                <Label>Incoming lot ID</Label>
                <Input value={incomingLotId} onChange={(e) => setIncomingLotId(e.target.value)} />
              </FormField>
              <FormField>
                <Label>Outgoing lot / pack ID</Label>
                <Input value={outgoingLotId} onChange={(e) => setOutgoingLotId(e.target.value)} />
              </FormField>
              <FormField>
                <Label>Date put into storage</Label>
                <Input type="date" value={storedOn} onChange={(e) => setStoredOn(e.target.value)} />
              </FormField>
              <FormField>
                <Label>Quantity</Label>
                <Input type="number" min="0" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
                <InputWarning result={check} input="quantity" />
              </FormField>
              <FormField>
                <Label>Unit</Label>
                <Input value={quantityUnit} onChange={(e) => setQuantityUnit(e.target.value)} placeholder="e.g. flats, lb" />
              </FormField>
              <FormField>
                <Label>Primary packaging</Label>
                <Input value={primaryPackaging} onChange={(e) => setPrimaryPackaging(e.target.value)} placeholder="e.g. clamshell" />
              </FormField>
              <FormField>
                <Label>Secondary packaging</Label>
                <Input value={secondaryPackaging} onChange={(e) => setSecondaryPackaging(e.target.value)} placeholder="e.g. flat, carton" />
              </FormField>
            </div>
          </FieldGroup>

          <div className="mt-8 space-y-3 rounded-lg bg-zinc-50 p-4 ring-1 ring-zinc-950/5 dark:bg-white/5 dark:ring-white/10">
            <label className="flex items-start gap-3">
              <Checkbox checked={phiMet} onChange={setPhiMet} className="mt-0.5" />
              <span className="text-sm text-zinc-700 dark:text-zinc-300">PHI/EAHD met — Forms H1 and H2 verified</span>
            </label>
            <label className="flex items-start gap-3">
              <Checkbox checked={siteAssessed} onChange={setSiteAssessed} className="mt-0.5" />
              <span className="text-sm text-zinc-700 dark:text-zinc-300">Production site was assessed before harvest</span>
            </label>
            <label className="flex items-start gap-3">
              <Checkbox checked={packagingChecked} onChange={setPackagingChecked} className="mt-0.5" />
              <span className="text-sm text-zinc-700 dark:text-zinc-300">Packaging material checked and OK</span>
            </label>
            <label className="flex items-start gap-3">
              <Checkbox checked={rotatedAppropriately} onChange={setRotatedAppropriately} className="mt-0.5" />
              <span className="text-sm text-zinc-700 dark:text-zinc-300">Product is rotated appropriately (shelf life considered, e.g. first in, first out)</span>
            </label>
          </div>
          <EntryCheckPanel result={check} formCode="Q" />
        </DialogBody>
        <DialogActions>
          <div className="sm:mr-auto">
            <ComplianceBadge framework="CanadaGAP" formCode="Q" formName={FORM_NAME} placement="above" {...checkBadgeProps(check)} />
          </div>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export function PackingRecordsPage() {
  const { farmId } = useParams<{ farmId: string }>()
  const { org } = useAuth()
  const [year, setYear] = useState(new Date().getFullYear())
  const [dialog, setDialog] = useState<DialogState<PackingRecord>>(null)
  const saved = useSavedEntries(dialog !== null, year)

  const { logs, loading, error, addLog, updateLog } = usePackingRecords(org?.id, farmId, year)
  const { status, reload: reloadStatus } = useCanadaGapFarmStatus(org?.id, farmId, year)

  async function save(input: PackingRecordInput) {
    const log = dialog?.log ? await updateLog(dialog.log.id, input) : await addLog(input)
    await reloadStatus()
    if (log) saved.setIds([log.id])
  }

  const entries = [...logs].sort(byDateDesc((r) => r.packedOn)).map((log) => ({
    key: log.id,
    date: log.packedOn,
    onClick: () => setDialog({ log }),
    content: (
      <>
        <p className="mt-0.5 text-sm text-zinc-700 dark:text-zinc-300">
          {[log.productVariety, log.outgoingLotId ?? log.incomingLotId ?? log.fieldOrLotRef].filter(Boolean).join(' · ') || 'Packing record'}
          {log.quantity != null && (
            <span className="text-zinc-400 dark:text-zinc-500">
              {' '}
              · {log.quantity} {log.quantityUnit}
            </span>
          )}
        </p>
        {log.handledByName && <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">{log.handledByName}</p>}
        <EntryIssues issue={issueFor(status?.forms.q, log.id)} />
      </>
    ),
  }))

  return (
    <FarmLogLayout
      orgId={org?.id}
      farmId={farmId}
      form="q"
      title="Packing and storing"
      formName={FORM_NAME}
      addLabel="Record packing"
      onAdd={() => setDialog({})}
      year={year}
      onYearChange={setYear}
      status={status}
      loading={loading}
      error={error}
      entries={entries}
      emptyText={`No packing records in ${year}.`}
      saved={saved}
    >
      {dialog && <PackingRecordDialog key={dialog.log?.id ?? 'new'} onClose={() => setDialog(null)} initial={dialog.log} onSave={save} />}
    </FarmLogLayout>
  )
}
